import authService, { User as AuthUser } from '@/services/authService';
import type { StoredUserRole } from '../types';

export const BASE_USER_ROLES = [
  { value: 'student', label: 'Học sinh' },
  { value: 'teacher', label: 'Giáo viên' },
  { value: 'publisher', label: 'Nhà xuất bản' },
] as const;

export const ADMIN_USER_ROLES = [
  { value: 'admin', label: 'Quản trị viên' },
  { value: 'super_admin', label: 'Super Admin' },
] as const;

export type AssignableRole =
  | 'student'
  | 'teacher'
  | 'publisher'
  | 'admin'
  | 'super_admin';

export const isSuperAdmin = (user: AuthUser | null | undefined): boolean => {
  const roles = authService.getUserRoles(user ?? null);
  return roles.includes('super_admin') || roles.includes('root');
};

export const getAssignableRoles = (currentUser: AuthUser | null | undefined) => {
  if (isSuperAdmin(currentUser)) {
    return [...BASE_USER_ROLES, ...ADMIN_USER_ROLES];
  }
  return [...BASE_USER_ROLES];
};

export const resolveEditableRole = (user: {
  role?: string;
  roles?: string[];
  type?: string;
}): AssignableRole => {
  const roles = user.roles ?? [];
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('admin') || user.role === 'admin') return 'admin';
  if (user.role === 'teacher' || user.role === 'publisher' || user.role === 'student') {
    return user.role;
  }
  // Tài khoản nhập từ Excel không có `role`; `type` là chỗ duy nhất còn nói được
  // họ là ai. Bỏ qua nó thì form hiện Học sinh, và lần lưu kế tiếp ghi đè cả
  // role lẫn type theo giá trị sai đó.
  if (user.type === 'teacher' || user.type === 'student') {
    return user.type;
  }
  return 'student';
};

export const buildRolePayload = (selectedRole: AssignableRole): {
  role: StoredUserRole;
  roles: string[];
} => {
  if (selectedRole === 'super_admin') {
    return {
      role: 'admin' as const,
      roles: ['super_admin', 'admin'],
    };
  }

  if (selectedRole === 'admin') {
    return {
      role: 'admin' as const,
      roles: ['admin'],
    };
  }

  return {
    role: selectedRole,
    roles: [selectedRole],
  };
};

/**
 * `type` không có ô nhập trên form, nên nó phải bám theo vai trò đã chọn thay vì
 * mang theo giá trị mặc định. Vai trò ngoài học sinh/giáo viên (admin,
 * publisher) thì không có `type` tương ứng — API chỉ nhận `in:student,teacher` —
 * nên trả về rỗng để `type` sẵn có của tài khoản đi qua nguyên vẹn.
 */
export const buildTypePayload = (
  selectedRole: AssignableRole
): { type?: 'student' | 'teacher' } => {
  if (selectedRole === 'student' || selectedRole === 'teacher') {
    return { type: selectedRole };
  }

  return {};
};

export const getRoleLabel = (role: string, roles?: string[]): string => {
  if (roles?.includes('super_admin')) return 'Super Admin';
  const allRoles = [...BASE_USER_ROLES, ...ADMIN_USER_ROLES];
  return allRoles.find((item) => item.value === role)?.label || role;
};
